import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { cleanWilmanText } from '../lib/wilmanText.js';

const TRIP_DEFAULT = 'juneau-2026';

const MODES = [
  { id: 'chronological', label: 'Chronological' },
  { id: 'by_challenge', label: 'By challenge' },
  { id: 'by_person', label: 'By person' },
  { id: 'highlight', label: 'Highlight reel' },
  { id: 'final_cut', label: 'Final cut · narration' },
  { id: 'map', label: 'Map · locations' },
];

const ADVANCE_MS = 4500; // chronological auto-advance step

export default function Slideshow() {
  const params = useParams();
  const tripId = params.tripId || TRIP_DEFAULT;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('chronological');

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`/api/slideshow/data?trip_id=${tripId}`, {
        credentials: 'omit',
      });
      const ct = res.headers.get('content-type') || '';
      if (!res.ok || !ct.includes('json')) {
        setError(`slideshow data unavailable (${res.status})`);
        return;
      }
      const json = await res.json();
      setData(json);
      setError(null);
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, [tripId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Live-mode polling: refresh every 30s while we don't have a final
  // bundle. Stops once trip-end has fired and bundle.mode === 'final'.
  useEffect(() => {
    if (data?.mode === 'final') return undefined;
    const id = setInterval(refresh, 30000);
    return () => clearInterval(id);
  }, [data?.mode, refresh]);

  // Submission frames (challenge-tagged): always carry kind='submission'.
  // Trip Roll frames carry kind='trip_roll'. Merging happens per-mode
  // via framesForMode() so by_challenge can exclude trip_roll while
  // chronological / final-cut / by_person / map / highlight include it
  // (highlight excludes implicitly because trip_roll has no points).
  const submissions = useMemo(
    () =>
      (data?.submissions || [])
        .filter((s) => s.photo_url || (s.photo_urls?.length || 0) > 0)
        .map((s) => ({ ...s, kind: 'submission' })),
    [data?.submissions]
  );
  const tripRoll = useMemo(
    () => (data?.trip_roll || []).filter((r) => r.photo_url),
    [data?.trip_roll]
  );

  return (
    <div className="min-h-screen bg-bg-primary text-fg-primary">
      <header className="border-b border-border-tertiary bg-bg-primary/95 px-4 py-3 backdrop-blur">
        <div className="mx-auto flex max-w-screen-md items-baseline justify-between gap-3">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-fg-tertiary">
              {data?.mode === 'final' ? 'final cut · permanent URL' : 'live · refreshes during the trip'}
            </p>
            <h1 className="mt-1 font-serif text-xl">Slideshow · {tripId}</h1>
          </div>
          <ModePicker mode={mode} onChange={setMode} hasNarration={!!data?.audio_url} />
        </div>
      </header>

      <main className="mx-auto max-w-screen-md p-4">
        {loading && (
          <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            …loading slideshow
          </p>
        )}
        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 font-mono text-[11px] text-danger">
            {error}
          </div>
        )}

        {data && submissions.length === 0 && tripRoll.length === 0 && (
          <EmptyState mode={data.mode} />
        )}

        {data && (submissions.length > 0 || tripRoll.length > 0) && (
          <Body
            mode={mode}
            data={data}
            submissions={submissions}
            tripRoll={tripRoll}
          />
        )}
      </main>
    </div>
  );
}

function ModePicker({ mode, onChange, hasNarration }) {
  return (
    <div className="flex flex-wrap gap-1">
      {MODES.map((m) => {
        const disabled = m.id === 'final_cut' && !hasNarration;
        return (
          <button
            key={m.id}
            type="button"
            disabled={disabled}
            onClick={() => onChange(m.id)}
            className={[
              'rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition disabled:opacity-40',
              mode === m.id
                ? 'border-action bg-action text-action-fg'
                : 'border-border-tertiary bg-bg-secondary text-fg-secondary hover:bg-bg-tertiary',
            ].join(' ')}
            title={disabled ? 'Available after trip-end fires' : ''}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}

function EmptyState({ mode }) {
  return (
    <div className="card mt-6 px-4 py-8 text-center">
      <p className="font-serif italic text-fg-secondary">
        No photo submissions yet.
      </p>
      <p className="mt-2 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {mode === 'final'
          ? 'The narrated cut will populate as submissions land.'
          : 'This page refreshes every 30 seconds while the trip is live.'}
      </p>
    </div>
  );
}

function Body({ mode, data, submissions, tripRoll }) {
  // Per-mode merge rules:
  //   chronological / final_cut / by_person / map → both
  //   by_challenge → submissions only (trip_roll has no challenge tag)
  //   highlight → submissions only (trip_roll has no points to rank by)
  const merged = useMemo(() => {
    const all = [...submissions, ...tripRoll];
    return all.sort((a, b) =>
      (a.submitted_at || '').localeCompare(b.submitted_at || '')
    );
  }, [submissions, tripRoll]);

  if (mode === 'final_cut') return <FinalCut data={data} submissions={merged} />;
  if (mode === 'highlight') return <HighlightReel submissions={submissions} />;
  if (mode === 'by_challenge') return <Grouped key="ch" submissions={submissions} groupBy="challenge_id" />;
  if (mode === 'by_person') return <Grouped key="pp" submissions={merged} groupBy="player_id" />;
  if (mode === 'map') return <MapList submissions={merged} />;
  return <Chronological submissions={merged} />;
}

// ---- Chronological --------------------------------------------------

function Chronological({ submissions }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setIdx((i) => (i + 1) % submissions.length), ADVANCE_MS);
    return () => clearInterval(id);
  }, [paused, submissions.length]);

  const cur = submissions[idx];

  const goPrev = useCallback(() => setIdx((i) => (i - 1 + submissions.length) % submissions.length), [submissions.length]);
  const goNext = useCallback(() => setIdx((i) => (i + 1) % submissions.length), [submissions.length]);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'ArrowRight') goNext();
      else if (e.key === ' ') {
        e.preventDefault();
        setPaused((p) => !p);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [goPrev, goNext]);

  return (
    <div className={fullscreen ? 'fixed inset-0 z-40 flex flex-col bg-black' : 'mt-4 space-y-3'}>
      <SlideFrame submission={cur} fullscreen={fullscreen} />
      <div className="flex items-center justify-between gap-2 px-1">
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
          {idx + 1} / {submissions.length}
        </span>
        <div className="flex items-center gap-1.5">
          <ControlBtn onClick={goPrev}>‹ prev</ControlBtn>
          <ControlBtn onClick={() => setPaused((p) => !p)}>{paused ? 'play' : 'pause'}</ControlBtn>
          <ControlBtn onClick={goNext}>next ›</ControlBtn>
          <ControlBtn onClick={() => setFullscreen((v) => !v)}>{fullscreen ? 'exit ⛶' : '⛶'}</ControlBtn>
        </div>
      </div>
      <Scrubber count={submissions.length} idx={idx} onPick={setIdx} />
    </div>
  );
}

function SlideFrame({ submission, fullscreen }) {
  const photo = submission.photo_url || submission.photo_urls?.[0];
  const isRoll = submission.kind === 'trip_roll';
  return (
    <figure
      className={[
        'relative overflow-hidden rounded-md border border-border-tertiary bg-black',
        fullscreen ? 'flex-1' : 'aspect-[4/3] w-full',
      ].join(' ')}
    >
      <img
        src={photo}
        alt={submission.challenge_title || submission.caption || submission.challenge_id || ''}
        className={fullscreen ? 'h-full w-full object-contain' : 'h-full w-full object-cover'}
        referrerPolicy="no-referrer"
      />
      <figcaption className="absolute inset-x-0 bottom-0 space-y-1 bg-gradient-to-t from-black/80 to-transparent p-3">
        <p className="font-mono text-[10px] uppercase tracking-wider text-white/80">
          {isRoll
            ? 'trip roll'
            : `${submission.challenge_title || submission.challenge_id} · ${
                submission.verified ? 'verified' : 'rejected'
              } · ${submission.points} pts`}
        </p>
        {submission.camera_caption && (
          <p className="font-mono text-[10px] tracking-wider text-white/60">
            {submission.camera_caption}
          </p>
        )}
        {isRoll && submission.caption && (
          <p className="font-serif text-sm italic text-white">{submission.caption}</p>
        )}
        {!isRoll && submission.wilman_commentary && (
          <p className="font-serif text-sm italic text-white">
            &ldquo;{cleanWilmanText(submission.wilman_commentary)}&rdquo;
          </p>
        )}
      </figcaption>
    </figure>
  );
}

function Scrubber({ count, idx, onPick }) {
  return (
    <input
      type="range"
      min={0}
      max={count - 1}
      value={idx}
      onChange={(e) => onPick(parseInt(e.target.value, 10))}
      className="w-full"
      aria-label="Slideshow position"
    />
  );
}

function ControlBtn({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full border border-border-tertiary bg-bg-secondary px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-secondary hover:bg-bg-tertiary"
    >
      {children}
    </button>
  );
}

// ---- Final Cut (chronological + audio) ------------------------------

function FinalCut({ data, submissions }) {
  const audioRef = useRef(null);
  const [t, setT] = useState(0);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return undefined;
    const onTime = () => setT(a.currentTime);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
    };
  }, []);

  // Pick the photo whose timing window covers `t`; fall back to evenly
  // distributed if the bundle's timing array is empty.
  const cur = useMemo(() => {
    if (data.timing?.length > 0) {
      const slot = data.timing.find((row) => t >= row.starts_at && t < row.ends_at);
      const sub = submissions.find((s) => s.id === slot?.submission_id);
      if (sub) return sub;
    }
    if (data.audio_duration && submissions.length > 0) {
      const idx = Math.min(submissions.length - 1, Math.floor((t / data.audio_duration) * submissions.length));
      return submissions[idx];
    }
    return submissions[0];
  }, [data.timing, data.audio_duration, submissions, t]);

  return (
    <div className="mt-4 space-y-3">
      <SlideFrame submission={cur} fullscreen={false} />
      <audio
        ref={audioRef}
        src={data.audio_url}
        controls
        preload="auto"
        className="w-full"
      />
      <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
        {playing ? 'narration playing' : 'tap play to start the final cut'}
      </p>
      {data.script && (
        <details className="rounded-md border border-border-tertiary bg-bg-secondary px-3 py-2">
          <summary className="cursor-pointer font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            view script
          </summary>
          <p className="mt-2 whitespace-pre-wrap font-serif text-sm italic text-fg-secondary">
            {cleanWilmanText(data.script)}
          </p>
        </details>
      )}
    </div>
  );
}

// ---- Highlight reel (top N by points) ------------------------------

function HighlightReel({ submissions }) {
  const top = [...submissions]
    .filter((s) => s.verified)
    .sort((a, b) => (b.points || 0) - (a.points || 0))
    .slice(0, 12);
  if (top.length === 0) {
    return (
      <p className="mt-6 text-center font-serif italic text-fg-secondary">
        No verified submissions yet — the highlight reel waits.
      </p>
    );
  }
  return (
    <ul className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3">
      {top.map((s) => (
        <li key={s.id} className="card overflow-hidden">
          <div className="aspect-square bg-black">
            <img
              src={s.photo_url || s.photo_urls?.[0]}
              alt={s.challenge_title}
              className="h-full w-full object-cover"
              referrerPolicy="no-referrer"
              loading="lazy"
            />
          </div>
          <p className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
            +{s.points} · {s.challenge_title || s.challenge_id}
          </p>
        </li>
      ))}
    </ul>
  );
}

// ---- Grouped (by challenge / by person) -----------------------------

function Grouped({ submissions, groupBy }) {
  const groups = useMemo(() => {
    const map = new Map();
    for (const s of submissions) {
      const k = s[groupBy] || 'unknown';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(s);
    }
    return Array.from(map.entries());
  }, [submissions, groupBy]);

  return (
    <div className="mt-4 space-y-5">
      {groups.map(([key, items]) => (
        <section key={key}>
          <h2 className="mb-2 font-mono text-[11px] uppercase tracking-wider text-fg-secondary">
            {key} · {items.length}
          </h2>
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {items.map((s) => (
              <li key={s.id} className="card overflow-hidden">
                <div className="aspect-square bg-black">
                  <img
                    src={s.photo_url || s.photo_urls?.[0]}
                    alt=""
                    className="h-full w-full object-cover"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                </div>
                <p className="px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
                  {s.verified ? '✓' : '✕'} {s.points} pts
                </p>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

// ---- Map list ------------------------------------------------------

function MapList({ submissions }) {
  const located = submissions.filter(
    (s) => s.claimed_location && typeof s.claimed_location.lat === 'number'
  );
  if (located.length === 0) {
    return (
      <p className="mt-6 text-center font-serif italic text-fg-secondary">
        No GPS-tagged photos yet. (Mapbox-rendered map view lands in a polish pass.)
      </p>
    );
  }
  return (
    <ul className="mt-4 space-y-2">
      {located.map((s) => (
        <li key={s.id} className="card flex items-center gap-3 px-3 py-2">
          <img
            src={s.photo_url || s.photo_urls?.[0]}
            alt=""
            className="h-16 w-16 rounded object-cover"
            referrerPolicy="no-referrer"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[10px] uppercase tracking-wider text-fg-tertiary">
              {s.challenge_title || s.challenge_id}
            </p>
            <p className="font-mono text-xs text-fg-secondary">
              {s.claimed_location.lat.toFixed(4)}, {s.claimed_location.lng.toFixed(4)}
            </p>
          </div>
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${s.claimed_location.lat},${s.claimed_location.lng}`}
            target="_blank"
            rel="noreferrer noopener"
            className="font-mono text-[10px] uppercase tracking-wider text-action underline-offset-4 hover:underline"
          >
            open ↗
          </a>
        </li>
      ))}
    </ul>
  );
}
